import React, { useState } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface User {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount?: string;
  designation?: string;
  agentCode: string;
  currency?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}
interface UsersTableProps {
  users: User[];
  onEditUser: (user: User) => void;
  onViewAuditLog: (user: User) => void;
  onStatusChange?: (user: User, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: User) => void;
  onSyncFromBank?: (user: User) => void;
  onViewResetHistory?: (user: User) => void;
  onResendEmail?: (user: User) => void;
}
interface Record {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount?: string;
  designation?: string;
  agentCode: string;
  currency?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}
interface RecordsTableProps {
  records: Record[];
  onEditRecord: (record: Record) => void;
  onViewAuditLog: (record: Record) => void;
  onStatusChange?: (record: Record, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (record: Record) => void;
  onSyncFromBank?: (record: Record) => void;
  onViewResetHistory?: (record: Record) => void;
  onResendEmail?: (record: Record) => void;
}
const BRANCH_CODES = ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'];
const AGENT_CODES = ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'];
const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onEditUser,
  onViewAuditLog,
  onStatusChange,
  onResetPassword,
  onSyncFromBank,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  // Define columns
  const columns: Column<User>[] = [{
    id: 'fullName',
    label: 'Full Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.fullName} onChange={e => setFilters({
      ...filters,
      fullName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1892" data-mp="tjpwd1dw9wni">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1893" data-mp="xo5v8ip3c3t8" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1891" data-mp="1hac22m0ff4uc" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.username} onChange={e => setFilters({
      ...filters,
      username: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1895" data-mp="18afkgk2bzcad">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1896" data-mp="187031al6pr5s" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1894" data-mp="1qz2ocz8rtvmd" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.email} onChange={e => setFilters({
      ...filters,
      email: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1898" data-mp="314nghlr0o5au">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1899" data-mp="35x6tohlskyf4" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1897" data-mp="2tmpb9tfvbgdf" />
  }, {
    id: 'mobile',
    label: 'Mobile',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.mobile} onChange={e => setFilters({
      ...filters,
      mobile: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1901" data-mp="6hti7gnur251">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1902" data-mp="6gid9dio7occ" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1900" data-mp="2i6krh6ygy8d1" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || 'N/A'} size="small" sx={{
      backgroundColor: row.branchCode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(148, 163, 184, 0.1)',
      color: row.branchCode ? '#06B6D4' : '#94A3B8',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1903" data-mp="2ch1kqrnskre5" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1905" data-mp="37rg088cfs40b">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1906" data-mp="3ciy1kxidg6lt" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1904" data-mp="1tmcu7a0sejii" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1907" data-mp="3rhjn2of1zgh3">
          {row.settlementAccount || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.settlementAccount} onChange={e => setFilters({
      ...filters,
      settlementAccount: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1909" data-mp="2azm4esxjl1ah">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1910" data-mp="32ql1v8tv9z7g" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1908" data-mp="3hi14abavq0qm" />
  }, {
    id: 'designation',
    label: 'Designation',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1911" data-mp="2nu3t8pd2ayol">
          {row.designation || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.designation} onChange={e => setFilters({
      ...filters,
      designation: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1913" data-mp="82ziwnyn8iq5">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1914" data-mp="39dbba7c7517" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1912" data-mp="3gges9vl0srja" />
  }, {
    id: 'currency',
    label: 'Currency',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1915" data-mp="kiflraid00g9">
          {row.currency || 'PKR'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.currency || ''} onChange={e => setFilters({
      ...filters,
      currency: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1917" data-mp="18b5u79r201ls">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1918" data-mp="1d9h41khcmc40" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1916" data-mp="ui6k5na1izlm" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1919" data-mp="2j7li0nskgb70" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1920" data-mp="32wvyi404z066">
          <Select value={filters.status || 'all'} onChange={e => setFilters({
        ...filters,
        status: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1921" data-mp="36wejezryjb90">
            <MenuItem value="all" data-id="element-1922" data-mp="19xxh9t0uzea0">All</MenuItem>
            <MenuItem value="active" data-id="element-1923" data-mp="19zrzgzkqwsae">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1924" data-mp="156ywoh8u7u0y">Inactive</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderCell: row => new Date(row.createdAt).toLocaleDateString(),
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-1925" data-mp="v7gcq0sxg88w">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-1926" data-mp="1nxp0q9a59mve">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1927" data-mp="1ixkgc86487zw" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1928" data-mp="3n6gkxk4d40tg" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-1929" data-mp="2yt6odza34r69">
              {dateRangeError}
            </Typography>}
        </Box>
  }];
  // Apply filters
  const filteredUsers = users.filter(user => {
    if (filters.fullName && !user.fullName.toLowerCase().includes(filters.fullName.toLowerCase())) return false;
    if (filters.username && !user.username.toLowerCase().includes(filters.username.toLowerCase())) return false;
    if (filters.email && !user.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
    if (filters.mobile && !user.mobile.includes(filters.mobile)) return false;
    if (filters.branchCode && !user.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) return false;
    if (filters.settlementAccount && !user.settlementAccount?.toLowerCase().includes(filters.settlementAccount.toLowerCase())) return false;
    if (filters.designation && !user.designation?.toLowerCase().includes(filters.designation.toLowerCase())) return false;
    if (filters.status && user.status !== filters.status) return false;
    if (filters.dateFrom) {
      const userDate = new Date(user.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const userDate = new Date(user.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.fullName ? [{
    id: 'fullName',
    label: 'Name',
    value: filters.fullName,
    onDelete: () => setFilters({
      ...filters,
      fullName: ''
    })
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => setFilters({
      ...filters,
      username: ''
    })
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => setFilters({
      ...filters,
      email: ''
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => setFilters({
      ...filters,
      settlementAccount: ''
    })
  }] : []), ...(filters.designation ? [{
    id: 'designation',
    label: 'Designation',
    value: filters.designation,
    onDelete: () => setFilters({
      ...filters,
      designation: ''
    })
  }] : []), ...(filters.status ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  // Row actions
  const rowActions: RowAction<User>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: user => console.log('View user', user)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditUser
  }, {
    icon: 'lucide:refresh-cw',
    label: 'Sync from Bank',
    onClick: user => onSyncFromBank?.(user)
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: user => onResendEmail?.(user),
    condition: user => shouldShowResendEmail(user)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: user => onStatusChange?.(user, 'inactive'),
    condition: user => user.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: user => onStatusChange?.(user, 'active'),
    condition: user => user.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: user => onResetPassword?.(user)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: user => onViewResetHistory?.(user)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: onViewAuditLog
  }];
  return <DataTable title="Users" data={filteredUsers} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilters({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })} initialVisibleColumns={['fullName', 'username', 'email', 'mobile', 'branchCode', 'settlementAccount', 'designation', 'currency', 'status', 'createdAt']} onExport={() => console.log('Export CSV')} emptyStateMessage="No users found" data-id="element-1930" data-mp="204thbgxm86p0" />;
};
const RecordsTable: React.FC<RecordsTableProps> = ({
  records,
  onEditRecord,
  onViewAuditLog,
  onStatusChange,
  onResetPassword,
  onSyncFromBank,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  // Define columns
  const columns: Column<Record>[] = [{
    id: 'fullName',
    label: 'Full Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.fullName} onChange={e => setFilters({
      ...filters,
      fullName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1932" data-mp="vij8mze5ka72">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1933" data-mp="vhrbvl8tk6f7" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1931" data-mp="1g6sjrwjolx4r" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.username} onChange={e => setFilters({
      ...filters,
      username: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1935" data-mp="1vjeawqsicks9">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1936" data-mp="1vkpdina7vmhs" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1934" data-mp="1j7c7p4q2my5w" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.email} onChange={e => setFilters({
      ...filters,
      email: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1938" data-mp="17t0s7qvtbyac">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1939" data-mp="2x76z04vuppns" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1937" data-mp="1crv9upi1ngsv" />
  }, {
    id: 'mobile',
    label: 'Mobile',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.mobile} onChange={e => setFilters({
      ...filters,
      mobile: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1941" data-mp="eey3238dgo5n">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1942" data-mp="ee672rav4oeu" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1940" data-mp="x57cny6hni7d" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || 'N/A'} size="small" sx={{
      backgroundColor: row.branchCode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(148, 163, 184, 0.1)',
      color: row.branchCode ? '#06B6D4' : '#94A3B8',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1943" data-mp="h9hsuae7f02" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1945" data-mp="1xcuezsr87n14">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1946" data-mp="224cgchx5vpmm" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1944" data-mp="2gziigtt4z4ao" />
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1947" data-mp="2cxnycht7rdlp">
          {row.settlementAccount || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.settlementAccount} onChange={e => setFilters({
      ...filters,
      settlementAccount: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1949" data-mp="1p3cukqqdrph9">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1950" data-mp="231uaw3dyhzp5" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1948" data-mp="2mos9t7jokgbs" />
  }, {
    id: 'designation',
    label: 'Designation',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1951" data-mp="3ae35iam6bkj2">
          {row.designation || '-'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.designation} onChange={e => setFilters({
      ...filters,
      designation: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1953" data-mp="4lbstxbv6y9n">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1954" data-mp="9h0vwan3exap" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1952" data-mp="3mxhz4yyme3g6" />
  }, {
    id: 'currency',
    label: 'Currency',
    renderCell: row => <Typography variant="body2" fontSize="0.85rem" data-id="element-1955" data-mp="ngim8ug1eim6">
          {row.currency || 'PKR'}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.currency || ''} onChange={e => setFilters({
      ...filters,
      currency: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1957" data-mp="2aigu1s57b7yq">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1958" data-mp="379j3mnce1pay" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1956" data-mp="2oc83z1mugtuz" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1959" data-mp="2ta9kpfwssh3a" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-1960" data-mp="1cofugdnqupkl">
          <Select value={filters.status || 'all'} onChange={e => setFilters({
        ...filters,
        status: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1961" data-mp="1hplrgnofsyzx">
            <MenuItem value="all" data-id="element-1962" data-mp="20gmnp3y6agfs">All</MenuItem>
            <MenuItem value="active" data-id="element-1963" data-mp="20gwfmx2s2xo4">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-1964" data-mp="25fzjie70uxui">Inactive</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderCell: row => new Date(row.createdAt).toLocaleDateString(),
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-1965" data-mp="b39n9pt6lcjy">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-1966" data-mp="2m7ze9g4e9e0g">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1967" data-mp="28hm7vi75i7d8" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-1968" data-mp="187uyiwt7wnez" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-1969" data-mp="2au88ddbb1mzt">
              {dateRangeError}
            </Typography>}
        </Box>
  }];
  // Apply filters
  const filteredRecords = records.filter(record => {
    if (filters.fullName && !record.fullName.toLowerCase().includes(filters.fullName.toLowerCase())) return false;
    if (filters.username && !record.username.toLowerCase().includes(filters.username.toLowerCase())) return false;
    if (filters.email && !record.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
    if (filters.mobile && !record.mobile.includes(filters.mobile)) return false;
    if (filters.branchCode && !record.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) return false;
    if (filters.settlementAccount && !record.settlementAccount?.toLowerCase().includes(filters.settlementAccount.toLowerCase())) return false;
    if (filters.designation && !record.designation?.toLowerCase().includes(filters.designation.toLowerCase())) return false;
    if (filters.status && record.status !== filters.status) return false;
    if (filters.dateFrom) {
      const recordDate = new Date(record.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (recordDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const recordDate = new Date(record.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (recordDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.fullName ? [{
    id: 'fullName',
    label: 'Name',
    value: filters.fullName,
    onDelete: () => setFilters({
      ...filters,
      fullName: ''
    })
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => setFilters({
      ...filters,
      username: ''
    })
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => setFilters({
      ...filters,
      email: ''
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => setFilters({
      ...filters,
      settlementAccount: ''
    })
  }] : []), ...(filters.designation ? [{
    id: 'designation',
    label: 'Designation',
    value: filters.designation,
    onDelete: () => setFilters({
      ...filters,
      designation: ''
    })
  }] : []), ...(filters.status ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  // Row actions
  const rowActions: RowAction<Record>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: record => console.log('View record', record)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditRecord
  }, {
    icon: 'lucide:refresh-cw',
    label: 'Sync from Bank',
    onClick: record => onSyncFromBank?.(record)
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: record => onResendEmail?.(record),
    condition: record => shouldShowResendEmail(record)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: record => onStatusChange?.(record, 'inactive'),
    condition: record => record.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: record => onStatusChange?.(record, 'active'),
    condition: record => record.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: record => onResetPassword?.(record)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: record => onViewResetHistory?.(record)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: onViewAuditLog
  }];
  return <DataTable title="Records" data={filteredRecords} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilters({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    currency: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })} initialVisibleColumns={['fullName', 'username', 'email', 'mobile', 'branchCode', 'settlementAccount', 'designation', 'currency', 'status', 'createdAt']} onExport={() => console.log('Export CSV')} emptyStateMessage="No records found" data-id="element-1970" data-mp="2jpogk79itkv9" />;
};
export default RecordsTable;